// Single source of truth for what slide types KulaLearn supports.
// Every consumer (TemplatePicker, createSlide, lessonImport, Designer prompt)
// reads from this registry. Add a new slide type by adding one entry here.
//
// See SLIDE_REGISTRY_PLAN.md for the migration plan and rationale.

import type {
  Slide,
  SlideTemplate,
  TextSlideLayout,
  ActivityType,
  SlideData,
  ActivitySlideData,
  TemplateInfo,
  TextSlideData,
  CoverSlideData,
  ImageSlideData,
  VideoSlideData,
  QuizSlideData,
  WebappSlideData,
  AttuneSlideData,
  CanvaSlideData,
  CodeSlideData,
  ReflectionSlideData,
  LikertSlideData,
  SurveyChoiceSlideData,
  FreeTextSlideData,
  DropdownSlideData,
  RankingSlideData,
  MatchingSlideData,
  RevealPredictSlideData,
  ScenarioSimulationSlideData,
  SpotTheErrorSlideData,
  DragSortSlideData,
  GalleryWalkSlideData,
  TimelineNavigatorSlideData,
  ConfidenceMeterSlideData,
  ProgressiveBuilderSlideData,
  GuidedSandboxSlideData,
  ScaffoldedInvestigationSlideData,
  PerspectiveLensesSlideData,
  TensionMapperSlideData,
  BeforeAfterBridgeSlideData,
  ClaimEvidenceSlideData,
  LayeredRevealSlideData,
  ReflectActSlideData,
  TeachBackSlideData,
  HotspotExplorerSlideData,
  SourceExaminationSlideData,
  MarkThisAnswerSlideData,
  EssayMarkingSlideData,
  RevisionExamSlideData,
  ReviseRunSlideData,
  BridgeSlideData,
  InteractiveSlotSlideData,
  GeneratedInteractiveSlideData,
} from './slideTypes';
import type { FeatureKey } from '../../../lib/featureFlags';
import { DEFAULT_BOARD } from '../../../lib/marking/board';
import {
  buildCover,
  buildTitle,
  buildText,
  buildTextImage,
  buildBullets,
  buildTwoColumn,
  buildMedia,
  buildCode,
  buildQuiz,
  buildLikert,
  buildSurvey,
  buildFreeText,
  buildDropdown,
  buildRanking,
  buildMatching,
  buildReflection,
  buildWebapp,
  buildCanva,
  buildVideo,
  buildDoorSlot,
  TEXT_SLIDE_MAX_WORDS,
  type ImportLimits,
} from '../../../lib/designer/slideImporters';
import type { DesignerSlide } from '../../../lib/lessonImport';
import type { CognitiveLevel } from '../../../lib/masterySignal';
import { buildReviseRun } from '../../../lib/revise/importer';
import { answerKeyFor as reviseAnswerKey } from '../../../lib/revise/scoring';
import { workshopActivityKeys } from '../../../lib/revise/workshop/steps';
import { sourcePaperActivityKeys } from '../../../lib/revise/sourcePaper/steps';
import { assessedQuestions as reviseAssessed, mtaScreens as reviseMta, REVISE_RESULTS_KEY } from '../../../lib/revise/types';
import {
  buildRevealPredict,
  buildConfidenceMeter,
  buildReflectAct,
  buildBeforeAfterBridge,
  buildSpotTheError,
  buildClaimEvidence,
  buildGalleryWalk,
  buildPerspectiveLenses,
  buildScaffoldedInvestigation,
  buildLayeredReveal,
  buildTimelineNavigator,
  buildProgressiveBuilder,
  buildGuidedSandbox,
  buildDragSort,
  buildTensionMapper,
  buildScenarioSimulation,
  buildTeachBack,
  buildHotspotExplorer,
  buildSourceExamination,
  buildMarkThisAnswer,
  buildRevisionExam,
  buildBridge,
  buildInteractiveSlot,
} from '../../../lib/scenario-builder/importers';

export type RegistryCategory = 'content' | 'interactive' | 'embed';

// ---------------------------------------------------------------------------
// Interaction metadata (Learning Interaction Framework — Phase 0)
// ---------------------------------------------------------------------------

export type InteractionTier = 'graded' | 'exploration';

export type InteractionVerb =
  | 'viewed'
  | 'started'
  | 'responded'
  | 'submitted'
  | 'revised'
  | 'abandoned'
  | 'completed';

/**
 * Loose JSON-schema shape used to validate the `response` JSONB cell at write
 * time. Intentionally minimal — we don't ship a full schema validator; the
 * service layer asserts required keys and primitive types only.
 */
export type ResponseSchema =
  | { type: 'object'; required?: string[]; properties?: Record<string, ResponseSchema>; additionalProperties?: boolean }
  | { type: 'array'; items?: ResponseSchema; minItems?: number; maxItems?: number }
  | { type: 'string' }
  | { type: 'number'; minimum?: number; maximum?: number }
  | { type: 'integer'; minimum?: number; maximum?: number }
  | { type: 'boolean' };

/**
 * The shape returned by `answerKey(data)` for graded slides. The keys are
 * `activity_key`s within the slide; values are the correct answer in whatever
 * shape the slide expects (string for quiz, array for ranking, etc.).
 */
export type AnswerKey = Record<string, unknown>;

export interface InteractionMeta {
  /** Static tier, or a function of slide data when tier depends on `mode` etc. */
  tier: InteractionTier | ((data: any) => InteractionTier);
  verbs: InteractionVerb[];
  responseSchema: ResponseSchema;
  /** Graded slides only — returns { activity_key → correct value }. */
  answerKey?: (data: any) => AnswerKey;
  defaultCognitiveLevel?: CognitiveLevel;
  /**
   * Slides that *could* produce multiple rows per attempt declare their keys
   * here. Used at catalogue time (Designer prompt discovery) and as the
   * fan-out source when the slide saves all sub-activities in one shot
   * (reflection pattern: one onActivityResponse with `Record<questionId, text>`).
   */
  activityKeyExtractor?: (data: any) => string[];
  /**
   * Some slides fire one onActivityResponse PER sub-activity (e.g. reveal-predict
   * locks one prediction at a time). When set, dual-write reads the activity_key
   * directly from the response payload and writes a single row — no fan-out.
   * Return null to fall back to fan-out behaviour for that call.
   */
  responseToActivityKey?: (response: Record<string, unknown>) => string | null;
}

/** How the Designer JSON encodes interactive options for this slide type. */
export type OptionsShape =
  | 'none'              // no options field used
  | 'string-array'      // ["Option A", "Option B"]
  | 'quiz-objects'      // [{ text, correct, feedback }]
  | 'pair-tuples';      // pairs field: [["Left", "Right"], ...]

const createEssayMarkingDefaults = (): EssayMarkingSlideData => ({
  template: 'essay-marking',
  title: 'Write your essay',
  subtitle: '',
  // Stated rather than left absent, so a new slide's board is visible in the
  // saved content instead of being inferred from a missing field. Existing
  // slides have no board and resolve to this same value.
  board: DEFAULT_BOARD,
  topicId: '',
  year: 0,
  sitting: 'Nov',
  paper: '',
});

export interface DesignerMeta {
  /** Canonical Designer JSON label. The one rendered in the prompt's mapping table. */
  label: string;
  /** Alternate spellings the importer should also accept (e.g. 'TEXT + IMG'). */
  aliases?: string[];
  /** Hex colour used in the canvas card and the JSON `color` field. */
  color: string;
  /** Long-form description for the "PLATFORM SLIDE TYPES" section of the prompt. */
  promptDescription: string;
  /** Word/option-count guidance for the "TEXT LIMITS" section of the prompt. */
  wordLimit?: string;
  /**
   * Numeric ceiling for the slide's `text` word count, enforced (as an
   * advisory warning) at import time by `convertSlide`. The prompt-side
   * `wordLimit` string and this number must agree — a registry test asserts
   * the string contains the number. Only set on types whose limit describes
   * the prose `text` field; option-shaped limits stay per-importer.
   */
  maxWords?: number;
  /**
   * Advisory floor for content-bearing types: below this, the `text` is a
   * heading pretending to be a slide (the bare-heading fidelity-loss failure
   * mode). Only set where short text is never legitimate — cover/title
   * slides are MEANT to be short and must not carry one.
   */
  minWords?: number;
  /** Shape of the `options` (or `pairs`) field in the Designer JSON. */
  optionsShape: OptionsShape;
  /**
   * Function that converts a DesignerSlide into the corresponding SlideData.
   * `limits.maxWords` is this entry's `maxWords`; an importer that can
   * paginate (cover, title, text, image+text) returns several slides when the
   * text won't fit one, instead of letting it run off the slide.
   */
  importer: (ds: DesignerSlide, warnings: string[], limits?: ImportLimits) => SlideData | SlideData[];
}

export interface SlideRegistryEntry {
  /** Unique key for lookup. Convention: `${template}` or `${template}-${subtype}`. */
  key: string;
  template: SlideTemplate;
  layout?: TextSlideLayout;
  activityType?: ActivityType;

  // TemplatePicker UI
  displayName: string;
  shortDescription: string;
  icon: string;
  category: RegistryCategory;

  // Builder behaviour
  submittable: boolean;
  /** Empty-state SlideData used when the user adds a new slide of this type. */
  defaults: () => SlideData;

  designer: DesignerMeta;

  /**
   * Optional interaction metadata. Slides that capture learner data declare
   * this block so the learningInteractionService can validate, score, and
   * write one row per (attempt × activity_key × verb) into learning_interactions.
   * Slides without it are content (text, image, cover) and produce no rows.
   */
  interaction?: InteractionMeta;

  /**
   * When true, the entry is hidden from the TemplatePicker's primary grid.
   * Slides of this kind are reached through the AI build panel only. Used by
   * the scenario-builder family — humans don't manually configure their
   * 15-25 content fields; the agent pipeline does.
   */
  aiOnly?: boolean;

  /**
   * When true, no importer or renderer is wired up yet. The Lesson Designer's
   * system prompt can mention the type, but emitting it through the import
   * pipeline throws. Cleared per-template as Phase 2+ ships renderers.
   */
  comingSoon?: boolean;

  /**
   * When true, the entry is excluded from every AI-facing catalogue (the Lesson
   * Designer's "PLATFORM SLIDE TYPES" / JSON-mapping prompt sections). The slide
   * type still renders and captures interactions — it's just never *offered* by
   * the standard Create / Designer pipeline. Used by flag-gated, separate-surface
   * types like `source-examination` (revision_builder), which are authored only
   * through their own per-tenant feature-flagged UI. See REVISION_TEMPLATE_PLAN.md.
   */
  hiddenFromDesigner?: boolean;

  /**
   * Tenant entitlement gate. When set, this type only appears in the
   * TemplatePicker for tenants whose `useTenant().features[requiredFeature]` is
   * true. Used by Attune (`requiredFeature: 'attune'`). Unset = always available.
   */
  requiredFeature?: FeatureKey;
}

// ---------------------------------------------------------------------------
// The registry. Order here is the order tiles appear in TemplatePicker.
// ---------------------------------------------------------------------------

export const SLIDE_REGISTRY: SlideRegistryEntry[] = [
  // --- Content ---
  {
    key: 'cover',
    template: 'cover',
    displayName: 'Cover Page',
    shortDescription: 'Course title page with logo + branding',
    icon: 'BookOpen',
    category: 'content',
    submittable: false,
    defaults: (): CoverSlideData => ({
      template: 'cover',
      title: 'Course Title',
      subtitle: '',
      logoUrl: '',
      layout: 'centered',
      showAccentBar: true,
      darkOverlay: false,
    }),
    designer: {
      label: 'COVER',
      color: '#6c63ff',
      promptDescription: 'Title, Subtitle. Full-width branded title page.',
      wordLimit: '~40 words (title + subtitle)',
      maxWords: 40,
      optionsShape: 'none',
      importer: buildCover,
    },
  },
  {
    key: 'text-title',
    template: 'text',
    layout: 'title',
    displayName: 'Title',
    shortDescription: 'Big headline with optional subtitle',
    icon: 'Type',
    category: 'content',
    submittable: false,
    defaults: (): TextSlideData => ({
      template: 'text',
      layout: 'title',
      heading: 'Slide Title',
      body: '',
      alignment: 'center',
    }),
    designer: {
      label: 'TITLE',
      color: '#6c63ff',
      promptDescription: 'Title, Subtitle (optional). Big display headline.',
      wordLimit: '~30 words',
      maxWords: 30,
      optionsShape: 'none',
      importer: buildTitle,
    },
  },
  {
    key: 'text-body',
    template: 'text',
    layout: 'body',
    displayName: 'Text',
    shortDescription: 'Heading + body text',
    icon: 'FileText',
    category: 'content',
    submittable: false,
    defaults: (): TextSlideData => ({
      template: 'text',
      layout: 'body',
      heading: '',
      body: '',
    }),
    designer: {
      label: 'TEXT',
      color: '#4a90d9',
      promptDescription: 'Heading, Body. Workhorse content slide.',
      wordLimit: '~115 words (no image)',
      maxWords: TEXT_SLIDE_MAX_WORDS,
      minWords: 5,
      optionsShape: 'none',
      importer: buildText,
    },
  },
  {
    key: 'text-bullets',
    template: 'text',
    layout: 'bullets',
    displayName: 'Key Points',
    shortDescription: 'Bulleted list',
    icon: 'List',
    category: 'content',
    submittable: false,
    defaults: (): TextSlideData => ({
      template: 'text',
      layout: 'bullets',
      heading: '',
      body: '<ul><li></li></ul>',
    }),
    designer: {
      label: 'KEY POINTS',
      aliases: ['KEYPOINTS', 'BULLETS'],
      color: '#4a90d9',
      promptDescription: 'Heading, Bullet Items. Bulleted list.',
      wordLimit: '~65 words across all bullets',
      maxWords: 65,
      minWords: 5,
      optionsShape: 'none',
      importer: buildBullets,
    },
  },
  {
    key: 'text-two-column',
    template: 'text',
    layout: 'two-column',
    displayName: 'Two Columns',
    shortDescription: 'Side-by-side content',
    icon: 'Columns',
    category: 'content',
    submittable: false,
    defaults: (): TextSlideData => ({
      template: 'text',
      layout: 'two-column',
      body: '',
      secondaryBody: '',
      ratio: '50-50',
    }),
    designer: {
      label: 'TWO COL',
      aliases: ['TWOCOL', 'TWO COLUMN'],
      color: '#4a90d9',
      promptDescription: 'Left content, Right content. Side-by-side layout.',
      optionsShape: 'none',
      importer: buildTwoColumn,
    },
  },
  {
    key: 'text-image-text',
    template: 'text',
    layout: 'image-text',
    displayName: 'Image + Text',
    shortDescription: 'Image with text alongside',
    icon: 'PanelRight',
    category: 'content',
    submittable: false,
    defaults: (): TextSlideData => ({
      template: 'text',
      layout: 'image-text',
      heading: '',
      body: '',
      imageUrl: '',
      imagePosition: 'right',
      imageSize: 'large',
    }),
    designer: {
      label: 'TEXT+IMG',
      aliases: ['TEXT + IMG', 'TEXTIMG'],
      color: '#2d9cdb',
      promptDescription: 'Heading, Body, Image. Image fills ~50% of screen.',
      wordLimit: '~65 words (image takes ~50% screen)',
      maxWords: 65,
      minWords: 5,
      optionsShape: 'none',
      importer: buildTextImage,
    },
  },
  {
    key: 'image',
    template: 'image',
    displayName: 'Image',
    shortDescription: 'Full-screen image',
    icon: 'Image',
    category: 'content',
    submittable: false,
    defaults: (): ImageSlideData => ({
      template: 'image',
      url: '',
      caption: '',
    }),
    designer: {
      label: 'MEDIA',
      aliases: ['IMAGE'],
      color: '#9b51e0',
      promptDescription: 'Image, Optional Caption. Full-screen media.',
      optionsShape: 'none',
      importer: buildMedia,
    },
  },
  {
    key: 'video',
    template: 'video',
    displayName: 'Video',
    shortDescription: 'Full-screen video',
    icon: 'Play',
    category: 'content',
    submittable: false,
    defaults: (): VideoSlideData => ({
      template: 'video',
      url: '',
      caption: '',
    }),
    designer: {
      label: 'VIDEO',
      color: '#9b51e0',
      promptDescription: 'Video URL (YouTube supported), Optional Caption. Full-screen video.',
      optionsShape: 'none',
      importer: buildVideo,
    },
  },
  {
    key: 'code',
    template: 'code',
    displayName: 'Code',
    shortDescription: 'Syntax-highlighted code',
    icon: 'Code',
    category: 'content',
    submittable: false,
    defaults: (): CodeSlideData => ({
      template: 'code',
      heading: '',
      code: '',
      language: 'javascript',
      showLineNumbers: true,
    }),
    designer: {
      label: 'CODE',
      color: '#4a90d9',
      promptDescription: 'Code block. Syntax-highlighted code display.',
      optionsShape: 'none',
      importer: buildCode,
    },
  },

  // --- Interactive ---
  {
    key: 'quiz',
    template: 'quiz',
    displayName: 'Quiz',
    shortDescription: 'Multiple choice quiz',
    icon: 'HelpCircle',
    category: 'interactive',
    submittable: false,
    defaults: (): QuizSlideData => ({
      template: 'quiz',
      question: '',
      options: [],
      explanation: '',
    }),
    designer: {
      label: 'QUIZ',
      color: '#eb5757',
      promptDescription: 'Question, 4 options (one correct + per-option feedback). Graded.',
      wordLimit: 'exactly 4 options, ~10 words each. Fourth option should test a misconception.',
      optionsShape: 'quiz-objects',
      importer: buildQuiz,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted'],
      responseSchema: {
        type: 'object',
        required: ['optionId'],
        properties: { optionId: { type: 'string' } },
      },
      answerKey: (data: QuizSlideData) => {
        const correct = data.options.find((o) => o.isCorrect);
        return { q: correct?.id ?? null };
      },
      defaultCognitiveLevel: 'L1_recall',
    },
  },
  {
    key: 'activity-likert',
    template: 'activity',
    activityType: 'likert',
    displayName: 'Likert Scale',
    shortDescription: 'Agreement scale (1–5)',
    icon: 'SlidersHorizontal',
    category: 'interactive',
    submittable: true,
    defaults: (): LikertSlideData => ({
      template: 'activity',
      activityType: 'likert',
      mode: 'survey',
      question: '',
      scaleLabels: { low: 'Strongly Disagree', high: 'Strongly Agree' },
      scaleSize: 5,
    }),
    designer: {
      label: 'LIKERT',
      color: '#eb5757',
      promptDescription: 'Statement, Low/High labels, 5 or 7 points. Not graded.',
      wordLimit: 'statement must be scannable at a glance',
      optionsShape: 'string-array',
      importer: buildLikert,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded'],
      responseSchema: {
        type: 'object',
        required: ['value'],
        properties: { value: { type: 'integer', minimum: 1, maximum: 7 } },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'activity-survey-choice',
    template: 'activity',
    activityType: 'survey-choice',
    displayName: 'Survey Choice',
    shortDescription: 'Unscored multiple choice',
    icon: 'ListChecks',
    category: 'interactive',
    submittable: true,
    defaults: (): SurveyChoiceSlideData => ({
      template: 'activity',
      activityType: 'survey-choice',
      mode: 'survey',
      question: '',
      options: [],
      allowMultiple: false,
    }),
    designer: {
      label: 'SURVEY',
      color: '#eb5757',
      promptDescription: 'Question, Options, Multi-select toggle. Not graded.',
      wordLimit: 'max 4 options, ~10 words each',
      optionsShape: 'string-array',
      importer: buildSurvey,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded'],
      responseSchema: {
        type: 'object',
        required: ['selectedOptions'],
        properties: {
          selectedOptions: { type: 'array', items: { type: 'string' } },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'activity-free-text',
    template: 'activity',
    activityType: 'free-text',
    displayName: 'Free Text',
    shortDescription: 'Open text response',
    icon: 'MessageSquare',
    category: 'interactive',
    submittable: true,
    defaults: (): FreeTextSlideData => ({
      template: 'activity',
      activityType: 'free-text',
      mode: 'survey',
      question: '',
      placeholder: 'Type your response...',
    }),
    designer: {
      label: 'FREE TXT',
      aliases: ['FREETXT', 'FREE TEXT'],
      color: '#f2994a',
      promptDescription: 'Prompt, Placeholder, Max length. Not graded.',
      wordLimit: 'keep prompt short',
      optionsShape: 'none',
      importer: buildFreeText,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded'],
      responseSchema: {
        type: 'object',
        required: ['text'],
        properties: { text: { type: 'string' } },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'activity-dropdown',
    template: 'activity',
    activityType: 'dropdown',
    displayName: 'Dropdown',
    shortDescription: 'Select from a list',
    icon: 'ChevronDown',
    category: 'interactive',
    submittable: true,
    defaults: (): DropdownSlideData => ({
      template: 'activity',
      activityType: 'dropdown',
      mode: 'survey',
      question: '',
      options: [],
    }),
    designer: {
      label: 'DROPDOWN',
      color: '#eb5757',
      promptDescription: 'Question, Options list. Not graded. Most space-efficient choice type.',
      wordLimit: 'max 5 options, ~8 words each',
      optionsShape: 'string-array',
      importer: buildDropdown,
    },
    interaction: {
      tier: (data: DropdownSlideData) => (data.mode === 'quiz' ? 'graded' : 'exploration'),
      verbs: ['viewed', 'responded', 'submitted'],
      responseSchema: {
        type: 'object',
        required: ['optionId'],
        properties: { optionId: { type: 'string' } },
      },
      answerKey: (data: DropdownSlideData) => {
        if (data.mode !== 'quiz') return {};
        const correct = data.options.find((o) => o.isCorrect);
        return { q: correct?.id ?? null };
      },
      defaultCognitiveLevel: 'L1_recall',
    },
  },
  {
    key: 'activity-ranking',
    template: 'activity',
    activityType: 'ranking',
    displayName: 'Ranking',
    shortDescription: 'Drag to reorder',
    icon: 'ArrowUpDown',
    category: 'interactive',
    submittable: true,
    defaults: (): RankingSlideData => ({
      template: 'activity',
      activityType: 'ranking',
      mode: 'survey',
      question: '',
      items: [],
    }),
    designer: {
      label: 'RANKING',
      color: '#eb5757',
      promptDescription: 'Instruction, Draggable items. Not graded.',
      wordLimit: 'max 5 items (4 safer), ~8 words each',
      optionsShape: 'string-array',
      importer: buildRanking,
    },
    interaction: {
      tier: (data: RankingSlideData) => (data.mode === 'quiz' ? 'graded' : 'exploration'),
      verbs: ['viewed', 'responded', 'submitted'],
      responseSchema: {
        type: 'object',
        required: ['order'],
        properties: { order: { type: 'array', items: { type: 'string' } } },
      },
      // Authored array order is the correct order.
      answerKey: (data: RankingSlideData) =>
        data.mode === 'quiz' ? { q: data.items.map((i) => i.id) } : {},
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'activity-matching',
    template: 'activity',
    activityType: 'matching',
    displayName: 'Mix & Match',
    shortDescription: 'Connect matching pairs',
    icon: 'Shuffle',
    category: 'interactive',
    submittable: true,
    defaults: (): MatchingSlideData => ({
      template: 'activity',
      activityType: 'matching',
      mode: 'quiz',
      question: '',
      pairs: [],
    }),
    designer: {
      label: 'MATCH',
      aliases: ['MATCHING'],
      color: '#eb5757',
      promptDescription: 'Instruction, Left-right pairs (right shuffled). Graded.',
      wordLimit: 'max 4 pairs. Left ~10 words, Right ~5 words',
      optionsShape: 'pair-tuples',
      importer: buildMatching,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted'],
      responseSchema: {
        type: 'object',
        required: ['pairs'],
        properties: {
          // Learner's pairing: [[leftId, rightId], ...]
          pairs: {
            type: 'array',
            items: { type: 'array', items: { type: 'string' } },
          },
        },
      },
      // The expert model: each pair's own id is the "correct" pairing of its
      // left and right. We project to { pairId → [left, right] } so scoring
      // compares pairs as unordered sets keyed by pair id.
      answerKey: (data: MatchingSlideData) => ({
        q: data.pairs.map((p) => [p.left, p.right]),
      }),
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'reflection',
    template: 'reflection',
    displayName: 'Reflection',
    shortDescription: 'Open-ended reflection prompts',
    icon: 'MessageCircle',
    category: 'interactive',
    submittable: true,
    defaults: (): ReflectionSlideData => ({
      template: 'reflection',
      heading: '',
      description: '',
      questions: [],
    }),
    designer: {
      label: 'REFLECTION',
      color: '#f2994a',
      promptDescription: 'Reflection prompt(s). Not graded. Distinct from Free Text.',
      wordLimit: '~20–40 words',
      maxWords: 40,
      optionsShape: 'none',
      importer: buildReflection,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded'],
      responseSchema: {
        type: 'object',
        required: ['questionId', 'text'],
        properties: {
          questionId: { type: 'string' },
          text: { type: 'string' },
        },
      },
      activityKeyExtractor: (data: ReflectionSlideData) =>
        data.questions.map((q) => q.id),
      defaultCognitiveLevel: 'L3_apply',
    },
  },

  // --- Embed ---
  {
    key: 'webapp',
    template: 'webapp',
    displayName: 'Webapp',
    shortDescription: 'Embed external app',
    icon: 'Globe',
    category: 'embed',
    submittable: true,
    defaults: (): WebappSlideData => ({
      template: 'webapp',
      url: '',
      title: '',
    }),
    // An embedded app's `checkpoint` events land in learning_interactions
    // through this meta (activityService.recordEmbedCheckpoint): one row per
    // (attempt, step), scored by the marks the app sent. Graded at the slide
    // level; an unassessed moment writes correctness n/a and no score. No
    // answerKey: the app marks, the lesson records.
    interaction: {
      tier: 'graded',
      verbs: ['submitted', 'responded'],
      responseSchema: { type: 'object', required: ['step', 'attemptId'] },
    },
    designer: {
      label: 'WEBAPP',
      color: '#9b51e0',
      promptDescription: 'Embed URL/code. Most powerful — simulations, AI companions, custom tools.',
      wordLimit: '~30–50 words intro',
      maxWords: 50,
      optionsShape: 'none',
      importer: buildWebapp,
    },
  },
  {
    key: 'canva',
    template: 'canva',
    displayName: 'Canva',
    shortDescription: 'Embed Canva presentation',
    icon: 'Presentation',
    category: 'embed',
    submittable: true,
    defaults: (): CanvaSlideData => ({
      template: 'canva',
      embedUrl: '',
      title: '',
    }),
    designer: {
      label: 'CANVA',
      color: '#9b51e0',
      promptDescription: 'Canva embed link.',
      optionsShape: 'none',
      importer: buildCanva,
    },
  },

  // --- Attune (personalised AI moment) ---
  // hiddenFromDesigner: manually addable in the picker, but not offered to the
  // AI Lesson Designer catalogue (flag-gated to entitled tenants in Phase 6).
  // The exploration `interaction` block makes the viewer treat it as
  // completion-gated and (Phase 2) mirrors a terminal learning_interactions row.
  {
    key: 'attune',
    template: 'attune',
    displayName: 'Attune',
    shortDescription: 'Personalised AI learning moment',
    icon: 'Sparkles',
    // Interactive (not embed): it belongs with the other AI/interactive moments
    // in the picker, beside the Experience Builder doorway. The viewer's embed
    // nav-hiding keys off the template id ('canva'/'webapp'), not this category.
    category: 'interactive',
    submittable: false,
    hiddenFromDesigner: true,
    requiredFeature: 'attune',
    defaults: (): AttuneSlideData => ({
      template: 'attune',
      audience: 'adult',
    }),
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'started', 'responded', 'completed'],
      responseSchema: { type: 'object' },
      defaultCognitiveLevel: 'L2_interpret',
    },
    designer: {
      label: 'ATTUNE',
      color: '#1d9e75',
      promptDescription: 'Personalised AI learning moment (hook / facilitator / experience).',
      optionsShape: 'none',
      importer: (): AttuneSlideData => ({ template: 'attune', audience: 'adult' }),
    },
  },

  // ---------------------------------------------------------------------------
  // Scenario builder (Kula Create) templates — retired from authoring (Aug
  // 2026, Activity Doors are the standard; `experience_builder` re-enables
  // Create per tenant). All are aiOnly (no TemplatePicker tile) AND
  // hiddenFromDesigner: the Lesson Designer's auto-derived catalogue used to
  // advertise these labels, which invited it to emit typed experience slides
  // that import as empty shells — the Designer plans interactive moments as
  // Door slides (or, on the retired flag-off branch, Interactive slots), never
  // as pre-typed experiences. Renderers, importers and interaction blocks all
  // stay: existing lessons contain these templates and must keep rendering,
  // and `findRegistryByDesignerLabel` still resolves the labels on import.
  // ---------------------------------------------------------------------------
  {
    key: 'reveal-predict',
    template: 'reveal-predict',
    displayName: 'Reveal & Predict',
    shortDescription: 'Predict, then see the answer',
    icon: 'EyeOff',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    // comingSoon cleared in Phase 2 — renderer, dispatch, importer all wired.
    defaults: (): RevealPredictSlideData => ({
      template: 'reveal-predict',
      title: 'New prediction set',
      subtitle: '',
      predictions: [],
      summary: { prompt: '' },
    }),
    designer: {
      label: 'REVEAL-PREDICT',
      color: '#eb5757',
      promptDescription: 'Misconception buster. Learner predicts before the answer is revealed; the gap strengthens encoding.',
      wordLimit: '3-6 predictions; each question scannable; feedback specific to the choice',
      optionsShape: 'none',
      importer: buildRevealPredict,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      activityKeyExtractor: (data: RevealPredictSlideData) => data.predictions.map((p) => p.id),
      // Each prediction lock fires its own onActivityResponse with the prediction
      // id baked into the payload — dual-write writes a single row keyed on it.
      responseToActivityKey: (response) =>
        typeof response.predictionId === 'string' ? response.predictionId : null,
      responseSchema: {
        type: 'object',
        required: ['predictionId', 'chosen'],
        properties: {
          predictionId: { type: 'string' },
          chosen: { type: 'string' },
        },
      },
      answerKey: (data: RevealPredictSlideData) =>
        Object.fromEntries(data.predictions.map((p) => [p.id, p.correctValue])),
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'scenario-simulation',
    template: 'scenario-simulation',
    displayName: 'Scenario Simulation',
    shortDescription: 'Branching decision paths with consequences',
    icon: 'Compass',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): ScenarioSimulationSlideData => ({
      template: 'scenario-simulation',
      title: 'New scenario',
      subtitle: '',
      branches: [],
    }),
    designer: {
      label: 'SCENARIO-SIM',
      color: '#eb5757',
      promptDescription: 'Branching decision paths. Each choice leads to a different next branch and (eventually) an ending.',
      optionsShape: 'none',
      importer: buildScenarioSimulation,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // ONE composite journey record per completed run — `{ path, endingId,
      // reflection? }` — emitted when the learner lands on a terminal branch.
      // Both renderers (standard Student and NeonOracleScenarioSim) emit this
      // shape; per-decision capture is gone because records are keyed per
      // slide, so per-choice writes overwrote each other and only the last
      // decision survived. The full path lives inside the one record instead.
      // No activityKeyExtractor: there is exactly one keyed row per journey.
      responseToActivityKey: (response) =>
        typeof response.endingId === 'string' ? 'journey' : null,
      responseSchema: {
        type: 'object',
        required: ['path', 'endingId'],
        properties: {
          path: { type: 'array' },
          endingId: { type: 'string' },
          reflection: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'spot-the-error',
    template: 'spot-the-error',
    displayName: 'Spot the Error',
    shortDescription: 'Find the mistakes among decoys',
    icon: 'Search',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    // comingSoon cleared in Phase 4 — renderer, dispatch, importer all wired.
    defaults: (): SpotTheErrorSlideData => ({
      template: 'spot-the-error',
      title: 'New error hunt',
      subtitle: '',
      sections: [],
    }),
    designer: {
      label: 'SPOT-ERROR',
      color: '#eb5757',
      promptDescription: 'Critical evaluation. Learner finds errors among decoys; each catch unlocks an explanation.',
      optionsShape: 'none',
      importer: buildSpotTheError,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // Single-row capture per slide — set comparison against all error ids.
      answerKey: (data: SpotTheErrorSlideData) => ({
        q: data.sections.flatMap((s) =>
          s.content.filter((c) => c.type === 'error' && c.errorId).map((c) => c.errorId!),
        ),
      }),
      responseSchema: {
        type: 'object',
        required: ['found'],
        properties: {
          found: { type: 'array', items: { type: 'string' } },
          missed: { type: 'array', items: { type: 'string' } },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'drag-sort',
    template: 'drag-sort',
    displayName: 'Drag & Sort',
    shortDescription: 'Classify or sequence by dragging',
    icon: 'LayoutGrid',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): DragSortSlideData => ({
      template: 'drag-sort',
      title: 'New drag-sort',
      subtitle: '',
      instruction: '',
      mode: 'categorise',
      items: [],
      zones: [],
      feedback: { allCorrect: '', partial: '' },
    }),
    designer: {
      label: 'DRAG-SORT',
      color: '#eb5757',
      promptDescription: 'Classification or sequencing by drag-and-drop. Items have correct zones (and optionally positions).',
      optionsShape: 'none',
      importer: buildDragSort,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      responseSchema: {
        type: 'object',
        required: ['placements'],
        properties: {
          placements: {
            type: 'array',
            items: {
              type: 'object',
              required: ['itemId', 'zoneId'],
              properties: {
                itemId: { type: 'string' },
                zoneId: { type: 'string' },
                position: { type: 'integer' },
              },
            },
          },
        },
      },
      // Correct placement = right zone, and (sequence mode) right position.
      // `position: null` means "zone only" — the scorer ignores order for it.
      answerKey: (data: DragSortSlideData) => ({
        q: data.items.map((i) => ({
          itemId: i.id,
          zoneId: i.correctZone,
          position: i.correctPosition ?? null,
        })),
      }),
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'gallery-walk',
    template: 'gallery-walk',
    displayName: 'Gallery Walk',
    shortDescription: 'Multi-station survey with reflections',
    icon: 'Images',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): GalleryWalkSlideData => ({
      template: 'gallery-walk',
      title: 'New gallery walk',
      subtitle: '',
      intro: '',
      stations: [],
      synthesis: { prompt: '' },
    }),
    designer: {
      label: 'GALLERY-WALK',
      color: '#f2994a',
      promptDescription: 'Stations of content with a reflection prompt each. Self-paced exploration.',
      optionsShape: 'none',
      importer: buildGalleryWalk,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      responseSchema: {
        type: 'object',
        required: ['visited'],
        properties: {
          visited: { type: 'array', items: { type: 'string' } },
          reflections: { type: 'object' },
          synthesis: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'timeline-navigator',
    template: 'timeline-navigator',
    displayName: 'Timeline Navigator',
    shortDescription: 'Chronological events with cause-effect',
    icon: 'CalendarDays',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): TimelineNavigatorSlideData => ({
      template: 'timeline-navigator',
      title: 'New timeline',
      subtitle: '',
      events: [],
      reflection: { prompt: '' },
    }),
    designer: {
      label: 'TIMELINE',
      color: '#f2994a',
      promptDescription: 'Chronological events with detail-on-expand. Makes cause-effect visible across time.',
      optionsShape: 'none',
      importer: buildTimelineNavigator,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      responseSchema: {
        type: 'object',
        required: ['visited'],
        properties: {
          visited: { type: 'array', items: { type: 'string' } },
          questionAnswers: { type: 'object' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'confidence-meter',
    template: 'confidence-meter',
    displayName: 'Confidence Meter',
    shortDescription: 'Pre/post confidence shifts',
    icon: 'Gauge',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    // comingSoon cleared in Phase 4 — renderer, dispatch, importer all wired.
    defaults: (): ConfidenceMeterSlideData => ({
      template: 'confidence-meter',
      title: 'New confidence meter',
      subtitle: '',
      items: [],
      summary: { prompt: '' },
    }),
    designer: {
      label: 'CONFIDENCE',
      color: '#f2994a',
      promptDescription: 'Metacognition. Rate confidence before, then after, encountering new information.',
      optionsShape: 'none',
      importer: buildConfidenceMeter,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: (data: ConfidenceMeterSlideData) => data.items.map((i) => i.id),
      // Each item locks fires its own onActivityResponse keyed by item id.
      responseToActivityKey: (response) =>
        typeof response.id === 'string' ? response.id : null,
      responseSchema: {
        type: 'object',
        required: ['id', 'pre', 'post'],
        properties: {
          id: { type: 'string' },
          pre: { type: 'integer' },
          post: { type: 'integer' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'progressive-builder',
    template: 'progressive-builder',
    displayName: 'Progressive Builder',
    shortDescription: 'Build layer by layer',
    icon: 'Layers',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): ProgressiveBuilderSlideData => ({
      template: 'progressive-builder',
      title: 'New builder',
      subtitle: '',
      baseState: '',
      layers: [],
      reflection: { prompt: '' },
    }),
    designer: {
      label: 'BUILDER',
      color: '#f2994a',
      promptDescription: 'Construct something layer by layer; each addition transforms the output.',
      optionsShape: 'none',
      importer: buildProgressiveBuilder,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      responseSchema: {
        type: 'object',
        required: ['layersCompleted'],
        properties: {
          layersCompleted: { type: 'array', items: { type: 'string' } },
          reflection: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'guided-sandbox',
    template: 'guided-sandbox',
    displayName: 'Guided Sandbox',
    shortDescription: 'Open practice with dimensional scoring',
    icon: 'PenLine',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): GuidedSandboxSlideData => ({
      template: 'guided-sandbox',
      title: 'New sandbox',
      subtitle: '',
      task: '',
      placeholder: '',
      minScore: 50,
      dimensions: [],
      guidance: {},
    }),
    designer: {
      label: 'SANDBOX',
      color: '#f2994a',
      promptDescription: 'Open writing/creation space with multi-dimensional heuristic scoring.',
      optionsShape: 'none',
      importer: buildGuidedSandbox,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      responseSchema: {
        type: 'object',
        required: ['submission'],
        properties: {
          submission: { type: 'string' },
          scores: { type: 'object' },
          overallScore: { type: 'number' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'scaffolded-investigation',
    template: 'scaffolded-investigation',
    displayName: 'Scaffolded Investigation',
    shortDescription: 'Three rounds of decreasing scaffolding',
    icon: 'Microscope',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): ScaffoldedInvestigationSlideData => ({
      template: 'scaffolded-investigation',
      title: 'New investigation',
      subtitle: '',
      rounds: [],
      debrief: { prompt: '' },
    }),
    designer: {
      label: 'INVESTIGATION',
      color: '#f2994a',
      promptDescription: 'Three rounds of probing content: guided, then mixed, then independent.',
      optionsShape: 'none',
      importer: buildScaffoldedInvestigation,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: (data: ScaffoldedInvestigationSlideData) => data.rounds.map((r) => r.id),
      responseToActivityKey: (response) => (typeof response.roundId === 'string' ? response.roundId : null),
      responseSchema: {
        type: 'object',
        required: ['roundId', 'probes'],
        properties: {
          roundId: { type: 'string' },
          probes: { type: 'array', items: { type: 'string' } },
          reflection: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'perspective-lenses',
    template: 'perspective-lenses',
    displayName: 'Perspective Lenses',
    shortDescription: 'Multi-stakeholder analysis',
    icon: 'Glasses',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): PerspectiveLensesSlideData => ({
      template: 'perspective-lenses',
      title: 'New lenses',
      subtitle: '',
      stimulus: { context: '' },
      lenses: [],
      synthesis: { prompt: '', showLensComparison: true },
    }),
    designer: {
      label: 'LENSES',
      color: '#f2994a',
      promptDescription: 'Analyse one stimulus from multiple stakeholder perspectives.',
      optionsShape: 'none',
      importer: buildPerspectiveLenses,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: (data: PerspectiveLensesSlideData) => data.lenses.map((l) => l.id),
      responseToActivityKey: (response) => (typeof response.lensId === 'string' ? response.lensId : null),
      responseSchema: {
        type: 'object',
        required: ['lensId'],
        properties: {
          lensId: { type: 'string' },
          answers: { type: 'object' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'tension-mapper',
    template: 'tension-mapper',
    displayName: 'Tension Mapper',
    shortDescription: 'Place factors on a continuum',
    icon: 'Scale',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): TensionMapperSlideData => ({
      template: 'tension-mapper',
      title: 'New tension',
      subtitle: '',
      tension: {
        statement: '',
        poleA: { label: '', description: '' },
        poleB: { label: '', description: '' },
      },
      factors: [],
      expertAnalysis: { summary: '', insights: [] },
      reflection: { prompt: '' },
    }),
    designer: {
      label: 'TENSION-MAPPER',
      color: '#f2994a',
      promptDescription: 'Place factors on a continuum between two poles. Surfaces trade-offs.',
      optionsShape: 'none',
      importer: buildTensionMapper,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: () => ['placements'],
      // Single-row template: route the whole `{ placements: [...] }` payload to
      // one 'placements' key. Without this the dual-write falls into the batched
      // reflection projection and mangles the array into a `text` field.
      responseToActivityKey: () => 'placements',
      responseSchema: {
        type: 'object',
        required: ['placements'],
        properties: {
          placements: {
            type: 'array',
            items: {
              type: 'object',
              required: ['factorId', 'position'],
              properties: {
                factorId: { type: 'string' },
                position: { type: 'number', minimum: 0, maximum: 100 },
              },
            },
          },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'hotspot-explorer',
    template: 'hotspot-explorer',
    displayName: 'Hotspot Explorer',
    shortDescription: 'Click zones on an image to uncover layered detail',
    icon: 'Map',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): HotspotExplorerSlideData => ({
      template: 'hotspot-explorer',
      title: 'New hotspot explorer',
      subtitle: '',
      imageUrl: '',
      imageAlt: '',
      hotspots: [],
      synthesis: { title: '', body: '', reflectionPrompt: '' },
    }),
    designer: {
      label: 'HOTSPOT',
      color: '#56ccf2',
      promptDescription: 'Image with clickable hotspots — spatial / diagram exploration. Best for anatomy, maps, process flows, systems.',
      optionsShape: 'none',
      importer: buildHotspotExplorer,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: () => ['explored'],
      responseSchema: {
        type: 'object',
        required: ['explored'],
        properties: {
          explored: { type: 'array', items: { type: 'string' } },
          explorationOrder: { type: 'array', items: { type: 'string' } },
          allExplored: { type: 'boolean' },
          reflection: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'source-examination',
    template: 'source-examination',
    displayName: 'Source Examination',
    shortDescription: 'Exam-style revision: a source + scaffolded L1/L2/L3 questions',
    icon: 'ScrollText',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    // Flag-gated separate surface (revision_builder) — never offered by the
    // standard Create / Lesson Designer pipeline. See REVISION_TEMPLATE_PLAN.md.
    hiddenFromDesigner: true,
    defaults: (): SourceExaminationSlideData => ({
      template: 'source-examination',
      title: 'New source examination',
      subtitle: '',
      timed: true,
      source: { label: 'Source 1A', type: '', body: '', attribution: '' },
      questions: [],
    }),
    designer: {
      label: 'SOURCE-EXAM',
      color: '#9b51e0',
      promptDescription:
        'Exam revision from a source document — one source plus a scaffolded ladder of L1 (recall) → L2 (interpret) → L3 (apply) questions, each with built-in skills coaching. Best for source-based subjects (History, English comprehension, RS).',
      optionsShape: 'none',
      importer: buildSourceExamination,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // One row per sub-question — activity_key is the question id, read straight
      // from the response payload (no fan-out: each submit fires one response).
      activityKeyExtractor: (data: SourceExaminationSlideData) => data.questions.map((q) => q.id),
      responseToActivityKey: (response) =>
        typeof response.questionId === 'string' ? response.questionId : null,
      responseSchema: {
        type: 'object',
        required: ['questionId', 'selected'],
        properties: {
          questionId: { type: 'string' },
          selected: { type: 'array', items: { type: 'integer' } },
          correct: { type: 'array', items: { type: 'integer' } },
          cognitiveLevel: { type: 'string' },
        },
      },
      // Per-question answer key — the correct option index(es) for each question id.
      answerKey: (data: SourceExaminationSlideData) =>
        Object.fromEntries(
          data.questions.map((q) => [q.id, Array.isArray(q.correct) ? [...q.correct].sort((a, b) => a - b) : [q.correct]]),
        ),
      defaultCognitiveLevel: 'L1_recall',
    },
  },
  {
    key: 'essay-marking',
    template: 'essay-marking',
    displayName: 'Essay Marking',
    shortDescription: 'Learner writes a Grade 12 History essay; the marking engine reads it',
    icon: 'PenLine',
    category: 'interactive',
    submittable: true,
    // Grade 12 History only — the engine marks essays against specific past
    // papers (NSC or IEB, per the slide's board), so the tile is noise anywhere
    // else. Authoring gate only: a learner is bounded by their marking
    // allowance regardless. Deliberately ONE flag for both boards — see
    // markingLedgerFeature in lib/marking/board.ts.
    requiredFeature: 'essay_marking',
    // Not offered to the Designer or the Create pipeline: the slide is bound to
    // a specific past-paper question with an extracted memo and misconceptions
    // brain behind it, so there is nothing for a model to invent. An author
    // picks from the engine's packs.
    hiddenFromDesigner: true,
    defaults: createEssayMarkingDefaults,
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'started', 'submitted', 'completed'],
      // One row per submission. There is no per-item key — the essay is the
      // unit, and it is marked whole.
      responseSchema: {
        type: 'object',
        required: ['topicId', 'essayChars'],
        properties: {
          // Which board's question this was. Recorded because {topicId, year,
          // paper} names a different question on each board — a response
          // without it cannot be read back unambiguously.
          board: { type: 'string' },
          topicId: { type: 'string' },
          year: { type: 'integer' },
          paper: { type: 'string' },
          essayChars: { type: 'integer' },
          // The band NAME, never a mark or a level: the engine's calibration is
          // validated against one human-marked script, so a number would be a
          // claim nobody can back.
          band: { type: 'string' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
    designer: {
      label: 'ESSAY-MARKING',
      color: '#7c3aed',
      promptDescription:
        'Learner writes a full CAPS History essay against a real past-paper question; the marking engine reads it and returns coached feedback. Bound to an extracted memo and misconceptions brain, so the question is chosen from a fixed list — never authored.',
      optionsShape: 'none',
      // hiddenFromDesigner, so nothing reaches this importer in practice. It
      // returns defaults rather than reading the payload: an invented topicId
      // would fail at the endpoint's pack gate anyway, and an author has to pick
      // a real question from the engine's list.
      importer: () => createEssayMarkingDefaults(),
    },
  },
  {
    key: 'mark-this-answer',
    template: 'mark-this-answer',
    displayName: 'Mark This Answer',
    shortDescription: 'Evaluate sample answers against a rubric (revision)',
    icon: 'ClipboardCheck',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    // Flag-gated revision companion (revision_builder) — never offered by the
    // standard Create / Lesson Designer pipeline. See REVISION_TEMPLATE_PLAN.md.
    hiddenFromDesigner: true,
    defaults: (): MarkThisAnswerSlideData => ({
      template: 'mark-this-answer',
      title: 'Mark this answer',
      subtitle: '',
      task: '',
      answers: [],
    }),
    designer: {
      label: 'MARK-THIS-ANSWER',
      color: '#9b51e0',
      promptDescription:
        'Evaluate sample answers against a rubric — the inverse, examiner skill. Revision companion to source-examination.',
      optionsShape: 'none',
      importer: buildMarkThisAnswer,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // One row per sample answer evaluated — activity_key read from the payload.
      activityKeyExtractor: (data: MarkThisAnswerSlideData) => data.answers.map((a) => a.id),
      responseToActivityKey: (response) =>
        typeof response.answerId === 'string' ? response.answerId : null,
      responseSchema: {
        type: 'object',
        required: ['answerId', 'judgements'],
        properties: {
          answerId: { type: 'string' },
          judgements: { type: 'object' },
          correctCount: { type: 'integer' },
          total: { type: 'integer' },
        },
      },
      answerKey: (data: MarkThisAnswerSlideData) =>
        Object.fromEntries(
          data.answers.map((a) => [a.id, Object.fromEntries(a.criteria.map((c) => [c.id, c.correct]))]),
        ),
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'revision-exam',
    template: 'revision-exam',
    displayName: 'Revision Exam',
    shortDescription: 'A whole exam-style revision paper in one self-contained slide',
    icon: 'ScrollText',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    // Flag-gated separate surface (revision_builder) — the single self-contained
    // revision interactive. Never offered by the standard Create / Designer
    // pipeline. See REVISION_TEMPLATE_PLAN.md.
    hiddenFromDesigner: true,
    defaults: (): RevisionExamSlideData => ({
      template: 'revision-exam',
      title: 'New revision exam',
      subtitle: '',
      timed: true,
      sources: [],
      questions: [],
    }),
    designer: {
      label: 'REVISION-EXAM',
      color: '#9b51e0',
      promptDescription:
        'A whole exam-style revision paper as one self-contained interactive — every source, a continuous L1→L3 question flow with skills coaching, timers, a unified results screen, and a 70%-unlock evaluative bonus. Built for source-based subjects.',
      optionsShape: 'none',
      importer: buildRevisionExam,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // One row per sub-question — activity_key read from the response payload
      // (each question fires its own response; the bonus fires per answer).
      activityKeyExtractor: (data: RevisionExamSlideData) => [
        ...data.questions.map((q) => q.id),
        ...(data.markThisAnswer?.answers.map((a) => a.id) ?? []),
      ],
      responseToActivityKey: (response) => {
        if (typeof response.questionId === 'string') return response.questionId;
        if (typeof response.answerId === 'string') return response.answerId;
        return null;
      },
      responseSchema: {
        type: 'object',
        properties: {
          questionId: { type: 'string' },
          answerId: { type: 'string' },
          selected: { type: 'array', items: { type: 'integer' } },
          correct: { type: 'array', items: { type: 'integer' } },
          cognitiveLevel: { type: 'string' },
          correctCount: { type: 'integer' },
        },
      },
      answerKey: (data: RevisionExamSlideData) =>
        Object.fromEntries(
          data.questions.map((q) => [q.id, Array.isArray(q.correct) ? [...q.correct].sort((a, b) => a - b) : [q.correct]]),
        ),
      defaultCognitiveLevel: 'L1_recall',
    },
  },
  {
    key: 'revise-run',
    template: 'revise-run',
    displayName: 'Revise run',
    shortDescription: 'A Kula Revise lesson, knowledge check, Practice Lab paper or skeleton builder, played end to end',
    icon: 'ScrollText',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    // Written by the Revise MCP (mcp/revise) from Dylan's and Leah's skills, or
    // by a future in-app panel; never offered by the Designer or the manual
    // picker. The content contract is lib/revise/types.ts. See
    // KULA-REVISE-LAB-ENGINE-PLAN.md.
    hiddenFromDesigner: true,
    defaults: (): ReviseRunSlideData => ({
      template: 'revise-run',
      title: 'New Revise run',
      subtitle: '',
      run: {
        version: 1,
        profile: 'lesson',
        board: 'ieb',
        cover: { eyebrow: 'Kula Revise', title: 'New run', lines: [], begin: 'Begin' },
        screens: [],
        results: { eyebrow: 'Report', title: 'Where you stand', bands: [{ min: 0, label: 'Done', text: '' }] },
      },
    }),
    designer: {
      label: 'REVISE-RUN',
      color: '#5b3fd1',
      promptDescription:
        'A complete Kula Revise run (lesson, knowledge check, exam paper or skeleton builder) as one self-contained interactive. Carries its own screens, scoring and results; authored by the Revise MCP against lib/revise/types.',
      optionsShape: 'none',
      importer: buildReviseRun,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // One row per assessed question and per Mark This Answer script (each
      // fires its own response carrying `questionId`), plus one `__results`
      // row on completion with the run summary.
      activityKeyExtractor: (data: ReviseRunSlideData) => [
        ...reviseAssessed(data.run).map((q) => q.id),
        ...reviseMta(data.run).map((m) => m.id),
        ...(data.run.workshop ? workshopActivityKeys(data.run.workshop) : []),
        ...(data.run.sourcePaper ? sourcePaperActivityKeys(data.run.sourcePaper) : []),
        REVISE_RESULTS_KEY,
      ],
      responseToActivityKey: (response) => (typeof response.questionId === 'string' ? response.questionId : null),
      responseSchema: {
        type: 'object',
        properties: {
          questionId: { type: 'string' },
          mode: { type: 'string' },
          selected: { type: 'array', items: { type: 'integer' } },
          sequence: { type: 'array', items: { type: 'integer' } },
          optionId: { type: 'string' },
          found: { type: 'array', items: { type: 'string' } },
          order: { type: 'array', items: { type: 'string' } },
          correct: { type: 'boolean' },
          marksEarned: { type: 'integer' },
          marksTotal: { type: 'integer' },
          pct: { type: 'integer' },
        },
      },
      answerKey: (data: ReviseRunSlideData) => reviseAnswerKey(data.run),
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'before-after-bridge',
    template: 'before-after-bridge',
    displayName: 'Before / After Bridge',
    shortDescription: 'Surface assumptions, then shift them',
    icon: 'ArrowRightLeft',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    // comingSoon cleared in Phase 4 — renderer, dispatch, importer all wired.
    defaults: (): BeforeAfterBridgeSlideData => ({
      template: 'before-after-bridge',
      title: 'New before/after',
      subtitle: '',
      beforePhase: { instruction: '', prompts: [] },
      contentPhase: { instruction: '', blocks: [] },
      afterPhase: { instruction: '', prompts: [] },
      comparison: { reflectionPrompt: '' },
    }),
    designer: {
      label: 'BEFORE-AFTER',
      color: '#f2994a',
      promptDescription: 'Capture beliefs before, present content, then capture beliefs after. The shift is the lesson.',
      optionsShape: 'none',
      importer: buildBeforeAfterBridge,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      // Single-row capture — the before/after pair is the unit of learning;
      // there's no useful per-prompt fan-out here.
      responseSchema: {
        type: 'object',
        required: ['before', 'after'],
        properties: {
          before: { type: 'object' },
          after: { type: 'object' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'claim-evidence',
    template: 'claim-evidence',
    displayName: 'Claim & Evidence',
    shortDescription: 'Build an argument from mixed evidence',
    icon: 'Gavel',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): ClaimEvidenceSlideData => ({
      template: 'claim-evidence',
      title: 'New claim',
      subtitle: '',
      claim: { statement: '' },
      evidencePool: [],
      argumentRules: { minEvidence: 1, maxEvidence: 5, requireReasoning: true },
      expertModel: { selectedEvidence: [], reasoning: '' },
      reflection: { prompt: '' },
    }),
    designer: {
      label: 'CLAIM-EVIDENCE',
      color: '#eb5757',
      promptDescription: 'Select strong evidence from a mixed pool and reason about why it supports the claim.',
      optionsShape: 'none',
      importer: buildClaimEvidence,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // The response's `selectedEvidence` array maps to the answer key's
      // `selectedEvidence` shape (set-equality scoring via the spot-the-error
      // path; we project the response onto `found` at score time).
      responseSchema: {
        type: 'object',
        required: ['selectedEvidence'],
        properties: {
          selectedEvidence: { type: 'array', items: { type: 'string' } },
          reasoning: { type: 'string' },
        },
      },
      answerKey: (data: ClaimEvidenceSlideData) => ({
        q: data.expertModel.selectedEvidence,
      }),
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'layered-reveal',
    template: 'layered-reveal',
    displayName: 'Layered Reveal',
    shortDescription: 'Reframe what the learner thought they knew',
    icon: 'Layers3',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): LayeredRevealSlideData => ({
      template: 'layered-reveal',
      title: 'New reveal',
      subtitle: '',
      scenario: { context: '' },
      layers: [],
      synthesis: { revealText: '', reflectionPrompt: '', showAssessmentTrail: true },
    }),
    designer: {
      label: 'LAYERED-REVEAL',
      color: '#f2994a',
      promptDescription: 'Each layer adds new context that changes the learner\'s interpretation of the last.',
      optionsShape: 'none',
      importer: buildLayeredReveal,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      activityKeyExtractor: (data: LayeredRevealSlideData) => data.layers.map((l) => l.id),
      responseToActivityKey: (response) => (typeof response.id === 'string' ? response.id : null),
      responseSchema: {
        type: 'object',
        required: ['id', 'assessment'],
        properties: {
          id: { type: 'string' },
          assessment: { type: 'object' },
        },
      },
      defaultCognitiveLevel: 'L2_interpret',
    },
  },
  {
    key: 'reflect-act',
    template: 'reflect-act',
    displayName: 'Reflect & Act',
    shortDescription: 'What / So What / Now What',
    icon: 'Sparkles',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    // comingSoon cleared in Phase 4 — renderer, dispatch, importer all wired.
    defaults: (): ReflectActSlideData => ({
      template: 'reflect-act',
      title: 'New reflection',
      subtitle: '',
      stages: [
        { id: 'what', label: 'What', description: '', prompts: [] },
        { id: 'so-what', label: 'So what', description: '', prompts: [] },
        { id: 'now-what', label: 'Now what', description: '', prompts: [] },
      ],
      summary: { showFullReflection: true, exportEnabled: false },
    }),
    designer: {
      label: 'REFLECT-ACT',
      color: '#f2994a',
      promptDescription: 'Structured debrief: What happened / So what does it mean / Now what will I do.',
      optionsShape: 'none',
      importer: buildReflectAct,
    },
    interaction: {
      tier: 'exploration',
      verbs: ['viewed', 'responded', 'completed'],
      // Each stage submit fires its own onActivityResponse keyed by stageId.
      activityKeyExtractor: () => ['what', 'so-what', 'now-what'],
      responseToActivityKey: (response) =>
        typeof response.stageId === 'string' ? response.stageId : null,
      responseSchema: {
        type: 'object',
        required: ['stageId', 'responses'],
        properties: {
          stageId: { type: 'string' },
          responses: { type: 'object' },
        },
      },
      defaultCognitiveLevel: 'L3_apply',
    },
  },
  {
    key: 'teach-back',
    template: 'teach-back',
    displayName: 'Teach-Back',
    shortDescription: 'Coach a struggling student: diagnose the belief, then catch the residue',
    icon: 'GraduationCap',
    category: 'interactive',
    submittable: true,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): TeachBackSlideData => ({
      template: 'teach-back',
      title: 'New teach-back',
      subtitle: '',
      instruction: '',
      student: { name: '', role: '', voiceOpening: '' },
      artifact: { kind: 'email', fragments: [] },
      misconceptionId: '',
      diagnosisOptions: [],
      coachingFragments: [],
      maxCoaching: 3,
      branches: [],
      defaultBranch: { outcome: 'partial', studentReply: '', coachNote: '' },
      revision: { kind: 'email', fragments: [] },
      expertNote: '',
    }),
    designer: {
      label: 'TEACH-BACK',
      color: '#eb5757',
      promptDescription:
        'The learner coaches a struggling student — red-pen the flawed work, diagnose the belief underneath, compose coaching from ordered fragments, then catch the residue in the rewrite. Protégé effect: teaching is the deepest test of understanding.',
      optionsShape: 'none',
      importer: buildTeachBack,
    },
    interaction: {
      tier: 'graded',
      verbs: ['viewed', 'responded', 'submitted', 'completed'],
      // Single composite record per activity — the whole teach-back trail is one
      // unit (diagnosis + coaching path + residue). Route it to one stable key so
      // the dual-write skips the batched-reflection fan-out.
      activityKeyExtractor: () => ['teach-back'],
      responseToActivityKey: () => 'teach-back',
      responseSchema: {
        type: 'object',
        required: ['diagnosisId', 'diagnosisCorrect', 'outcome', 'residueCaught'],
        properties: {
          struck: { type: 'array', items: { type: 'string' } },
          overStruck: { type: 'boolean' },
          diagnosisId: { type: 'string' },
          diagnosisCorrect: { type: 'boolean' },
          coachingPath: { type: 'array', items: { type: 'string' } },
          outcome: { type: 'string' },
          adapted: { type: 'boolean' },
          residueCaught: { type: 'boolean' },
        },
      },
      // Answer key: the correct diagnosis is the authored misconception.
      answerKey: (data: TeachBackSlideData) => ({ 'teach-back': data.misconceptionId }),
      defaultCognitiveLevel: 'L3_apply',
    },
  },

  // Interactive slot — placeholder unit awaiting Create (Phase C — Builder
  // redesign). Hidden from manual TemplatePicker; inserted via the Workbench
  // toolbar's `+ Interactive` button or by a Designer outline import (Phase D).
  // Phase E's Create walkthrough replaces the slot with the built scenario
  // group's slides via `applyScenarioGroupForSlot`.
  {
    key: 'interactive-slot',
    template: 'interactive-slot',
    displayName: 'Interactive Slot',
    shortDescription: 'Placeholder for an interactive moment, to be built in Create',
    icon: 'Zap',
    category: 'interactive',
    submittable: false,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): InteractiveSlotSlideData => ({
      template: 'interactive-slot',
      brief: '',
    }),
    designer: {
      label: 'INTERACTIVE-SLOT',
      aliases: ['INTERACTIVE'],
      color: '#D6451F',
      promptDescription:
        'Placeholder for an interactive moment. The Workbench renders it as a slot; Create fills it in later.',
      optionsShape: 'none',
      importer: buildInteractiveSlot,
    },
  },

  // Activity Door build (KULA-ACTIVITY-DOORS-TOP10.md, fork B) — a sealed
  // self-contained HTML interactive generated through a named door (Scenario,
  // Case Study, Mark the Work, Debate, Card Sort). aiOnly: the picker doesn't
  // offer this template directly — the flag-gated door cards in TemplatePicker
  // open a door intake, which inserts this slide pre-briefed. hiddenFromDesigner
  // keeps it out of the auto-derived slide-type catalogue: when a tenant has
  // activity_doors, the Lesson Designer instead emits it via the dedicated
  // ACTIVITY DOOR SLOTS prompt section as label "Door" (KULA-DOORS-SKETCH-PLAN.md),
  // and the import lands it briefed-not-built via buildDoorSlot.
  // Learner capture arrives via the embed bridge (the `embed_interactions`
  // pattern), not an `interaction` block — the generated HTML posts events out
  // of its sandboxed iframe like a webapp embed does.
  {
    key: 'generated-interactive',
    template: 'generated-interactive',
    displayName: 'Generated Interactive',
    shortDescription: 'A self-contained interactive built through an Activity Door',
    icon: 'DoorOpen',
    category: 'interactive',
    submittable: false,
    aiOnly: true,
    hiddenFromDesigner: true,
    requiredFeature: 'activity_doors',
    defaults: (): GeneratedInteractiveSlideData => ({
      template: 'generated-interactive',
      door: 'scenario',
      intake: {
        context: '',
        useLessonSource: false,
        goal: '',
        length: 'short',
        world: 'neon-oracle',
      },
    }),
    designer: {
      label: 'DOOR',
      aliases: ['GENERATED-INTERACTIVE'],
      color: '#D6451F',
      promptDescription:
        'Activity Door build — a named door (Scenario, Case Study, Mark the Work, Debate, Card Sort) generates a self-contained interactive from the slide brief.',
      optionsShape: 'none',
      importer: buildDoorSlot,
    },
  },

  // Bridge slide — auto-inserted narrative beat between scenario-group slides.
  // Not data-capturing (no interaction block).
  {
    key: 'bridge',
    template: 'bridge',
    displayName: 'Bridge',
    shortDescription: 'Narrative beat between scenario slides',
    icon: 'ArrowRight',
    category: 'content',
    submittable: false,
    aiOnly: true,
    hiddenFromDesigner: true,
    defaults: (): BridgeSlideData => ({
      template: 'bridge',
      narrativeText: '',
      showResponseRecap: true,
    }),
    designer: {
      label: 'BRIDGE',
      color: '#9b51e0',
      promptDescription: 'Auto-inserted between scenario-group slides. Narrative continuity, optional response recap.',
      optionsShape: 'none',
      importer: buildBridge,
    },
  },
];

/**
 * Stub importer for scenario-builder templates that don't yet have a renderer
 * wired up. The Lesson Designer's catalogue auto-derives these types via
 * `renderSlideTypeCatalogue()`, but the import pipeline gates them off until
 * each template's Phase 2+ port replaces this stub with `buildXxx()`.
 */
function stubImporter(templateKey: string) {
  return (): SlideData => {
    throw new Error(
      `Slide template "${templateKey}" is registered but not yet implemented. ` +
        `Land its Phase 2+ renderer + identity importer before emitting it.`,
    );
  };
}

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function findRegistryByKey(key: string): SlideRegistryEntry | undefined {
  return SLIDE_REGISTRY.find((e) => e.key === key);
}

export function findRegistryByTemplate(
  template: SlideTemplate,
  subtype?: TextSlideLayout | ActivityType,
): SlideRegistryEntry | undefined {
  return SLIDE_REGISTRY.find((e) => {
    if (e.template !== template) return false;
    if (template === 'text') return e.layout === subtype;
    if (template === 'activity') return e.activityType === subtype;
    return true;
  });
}

/**
 * Resolve a `Slide` (the runtime shape stored on lessons) to its registry entry,
 * accounting for the `text` and `activity` template families that have subtypes.
 */
export function findRegistryBySlide(slide: Slide): SlideRegistryEntry | undefined {
  const t = slide.data.template;
  const subtype: TextSlideLayout | ActivityType | undefined =
    t === 'text' ? (slide.data as TextSlideData).layout :
    t === 'activity' ? (slide.data as ActivitySlideData).activityType :
    undefined;
  return findRegistryByTemplate(t, subtype);
}

/** Match a Designer JSON `label` (case-insensitive) against canonical labels + aliases. */
export function findRegistryByDesignerLabel(label: string): SlideRegistryEntry | undefined {
  const upper = (label || '').toUpperCase().trim();
  return SLIDE_REGISTRY.find(
    (e) =>
      e.designer.label.toUpperCase() === upper ||
      e.designer.aliases?.some((a) => a.toUpperCase() === upper),
  );
}

// ---------------------------------------------------------------------------
// Derived exports — replace the legacy constants from slideTypes.ts
// ---------------------------------------------------------------------------

/**
 * TemplatePicker tile metadata. Derived from SLIDE_REGISTRY. `aiOnly` entries
 * are excluded — those templates are reached via the AI build panel, not the
 * manual grid (KULA_BUILDER_INTEGRATION.md §Author experience).
 */
export const templateInfo: TemplateInfo[] = SLIDE_REGISTRY
  .filter((e) => !e.aiOnly)
  .map((e) => ({
    id: e.template,
    key: e.key,
    layout: e.layout,
    activityType: e.activityType,
    name: e.displayName,
    description: e.shortDescription,
    icon: e.icon,
    category: e.category,
    requiredFeature: e.requiredFeature,
  }));

/**
 * Templates whose student response routes to the instructor grading inbox
 * (the `submissions` table → /admin/submissions, /instructor/submissions).
 *
 * Only `activity` and `reflection` are wired end-to-end (see SlideCanvas —
 * these are the two cases that call `onSubmission`). Experience/scenario
 * templates are intentionally NOT teacher-gradable: offering the toggle there
 * created a dead control that collected nothing. If you wire a new template to
 * `onSubmission`, add it here. (The registry's per-entry `submittable` field is
 * now informational only; this allowlist is the single gate.)
 */
export const SUBMITTABLE_TEMPLATES: SlideTemplate[] = ['activity', 'reflection'];

/**
 * Create a new slide with default data for the given template (and optional subtype
 * for `text` layouts and `activity` types). Looks up the registry entry and clones
 * its `defaults()`. Falls back to `text-body` if the template/subtype isn't found.
 */
export function createSlide(
  template: SlideTemplate,
  order: number,
  subtype?: TextSlideLayout | ActivityType,
): Slide {
  const id = crypto.randomUUID();
  const effectiveSubtype =
    subtype ?? (template === 'text' ? 'body' : template === 'activity' ? 'likert' : undefined);

  const entry =
    findRegistryByTemplate(template, effectiveSubtype) ??
    findRegistryByKey('text-body')!;
  return { id, order, data: entry.defaults() };
}
